import React, { useMemo, useState } from 'react';
import { 
  CalendarDays, Clock, ClipboardCheck, CalendarCheck, AlertCircle, 
  Users, Briefcase, ChevronRight, ListFilter 
} from "lucide-react";
import { Skeleton } from '@mui/material';
import { StatCardSkeleton } from "./PageSkeletonLoaders";
import TimesheetSummaryTable from './TimesheetSummaryTable';
import AllProjectsModal from './AllProjectsModal';
import moment from 'moment';

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

export default function TimesheetAdminDashboard({
    rawTimesheetData = [],
    projects = [],
    employees = [],
    isApiLoading = false
}) {
    const [isAllProjectsModalOpen, setAllProjectsModalOpen] = useState(false);

    const stats = useMemo(() => {
        if (!rawTimesheetData || rawTimesheetData.length === 0) {
            return { totalDays: 0, totalHours: 0, totalProjects: 0, submittedEmployees: 0, totalEmployees: 0, missingSubmissions: 0 };
        }
        
        const uniqueDays = [...new Set(rawTimesheetData.map(d => d.WORK_DAY))];
        const uniqueProjects = [...new Set(rawTimesheetData.map(d => d.PROJECT_CODE))];
        const totalHours = rawTimesheetData.reduce((sum, d) => sum + d.WORK_HOURS, 0);

        const submittedEmployeesSet = new Set(rawTimesheetData.map(d => d.CODE || d.EMP_CODE || d.EMP_NAME));
        const submittedEmployees = submittedEmployeesSet.size;
        const totalEmployeesCount = employees?.filter((el) => el.STATUS === "Active" && el.DSR_EXCEPTION !== "Yes")?.length || 0;

        return {
            totalDays: uniqueDays.length,
            totalHours: totalHours,
            totalProjects: uniqueProjects.length,
            submittedEmployees: submittedEmployees,
            totalEmployees: totalEmployeesCount,
            pendingEmployees: Math.max(0, totalEmployeesCount - submittedEmployees)
        };
    }, [rawTimesheetData, employees]);

    const projectDistribution = useMemo(() => {
        if (!rawTimesheetData || rawTimesheetData.length === 0) return [];
        const projectHours = {};
        rawTimesheetData.forEach(d => {
            const name = projects.find(p => p.PROJECT_CODE === d.PROJECT_CODE)?.PROJECT_NAME || d.PROJECT_CODE;
            projectHours[name] = (projectHours[name] || 0) + d.WORK_HOURS;
        });
        return Object.entries(projectHours)
            .map(([name, hours]) => ({ name, hours }))
            .sort((a, b) => b.hours - a.hours);
    }, [rawTimesheetData, projects]);

    const pieData = useMemo(() => {
        if (!projectDistribution || projectDistribution.length === 0) return null;

        let finalLabels = [];
        let finalData = [];
        
        if (projectDistribution.length > 7) {
            finalLabels = projectDistribution.slice(0, 7).map(p => p.name);
            finalData = projectDistribution.slice(0, 7).map(p => p.hours);
            const othersHours = projectDistribution.slice(7).reduce((sum, p) => sum + p.hours, 0);
            if (othersHours > 0) {
                finalLabels.push(`Others (${projectDistribution.length - 7} Projects)`);
                finalData.push(othersHours);
            }
        } else {
            finalLabels = projectDistribution.map(p => p.name);
            finalData = projectDistribution.map(p => p.hours);
        }

        const pieColors = [
            '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', 
            '#06b6d4', '#f43f5e', '#14b8a6', '#84cc16', '#6366f1',
            '#64748b' 
        ];

        const totalHours = finalData.reduce((acc, val) => acc + val, 0);

        const legendData = finalLabels.map((label, i) => ({
            name: label,
            value: finalData[i],
            color: pieColors[i % pieColors.length],
        }));

        return {
            chartData: {
                labels: finalLabels,
                datasets: [
                    {
                        data: finalData,
                        backgroundColor: pieColors.slice(0, finalLabels.length),
                        borderWidth: 0,
                    },
                ],
            },
            legendData,
            totalHours,
        };
    }, [projectDistribution]);

    const barChartData = useMemo(() => {
        if (!rawTimesheetData || rawTimesheetData.length === 0) return null;

        const taskTypeHours = {};
        rawTimesheetData.forEach(d => {
            const type = d.Task_Type || 'Other';
            taskTypeHours[type] = (taskTypeHours[type] || 0) + d.WORK_HOURS;
        });

        // Sort by highest hours
        const sortedTypes = Object.entries(taskTypeHours).sort((a, b) => b[1] - a[1]);
        
        return {
            labels: sortedTypes.map(t => t[0]),
            datasets: [
                {
                    label: 'Hours',
                    data: sortedTypes.map(t => t[1]),
                    backgroundColor: '#3b82f6',
                    borderRadius: 4,
                    barThickness: 12,
                }
            ]
        };
    }, [rawTimesheetData]);

    const pieOptions = {
        maintainAspectRatio: false,
        responsive: true,
        cutout: '70%',
        devicePixelRatio: typeof window !== 'undefined' && window.devicePixelRatio > 1 ? window.devicePixelRatio : 2,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: '#000',
                titleFont: { size: 12, family: "'Inter', sans-serif", weight: '600' },
                bodyFont: { size: 13, family: "'Inter', sans-serif" },
                padding: 12,
                callbacks: {
                    title: function(context) {
                        const title = context[0].label || '';
                        const chunks = [];
                        let current = '';
                        const words = title.split(' ');
                        words.forEach(word => {
                            if ((current + word).length > 30) {
                                if (current) chunks.push(current.trim());
                                current = word + ' ';
                            } else {
                                current += word + ' ';
                            }
                        });
                        if (current.trim()) chunks.push(current.trim());
                        return chunks;
                    }
                }
            },
            datalabels: { display: false }
        }
    };

    const barOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        responsive: true,
        devicePixelRatio: typeof window !== 'undefined' && window.devicePixelRatio > 1 ? window.devicePixelRatio : 2,
        plugins: {
            legend: { display: false },
            tooltip: {
                titleFont: { family: "'Inter', sans-serif" },
                bodyFont: { family: "'Inter', sans-serif" },
            },
            datalabels: { display: false }
        },
        scales: {
            x: { 
                grid: { display: true, drawBorder: false, color: '#f1f5f9' },
                ticks: { font: { family: "'Inter', sans-serif", size: 12, weight: '500' }, color: '#475569' },
                border: { display: false },
                title: { display: true, text: 'Hours', font: { family: "'Inter', sans-serif", size: 11, weight: '600' }, color: '#64748b' }
            },
            y: { 
                grid: { display: false, drawBorder: false },
                ticks: { 
                    font: { family: "'Inter', sans-serif", size: 12, weight: '500' }, 
                    color: '#475569',
                    callback: function(value) {
                        const label = this.getLabelForValue(value) || '';
                        return label.length > 25 ? label.substring(0, 25) + '...' : label;
                    }
                },
                border: { display: false },
                title: { display: false, text: 'Task Types', font: { family: "'Inter', sans-serif", size: 11, weight: '600' }, color: '#64748b' }
            }
        }
    };

    return (
        <div className="flex flex-col gap-3">
            {/* Top Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {isApiLoading ? (
                    <>
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                    </>
                ) : (
                    <>
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                            <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center shrink-0">
                                <Users className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5">Employees Submitted</p>
                                <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">
                                    {stats.submittedEmployees}{" "}
                                    <span className="text-[14px] text-slate-400 font-medium">
                                        / {stats.totalEmployees}
                                    </span>
                                </h2>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                            <div className="w-10 h-10 rounded-2xl bg-indigo-50 flex items-center justify-center shrink-0">
                                <Clock className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5">Logged Hours</p>
                                <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">
                                    {stats.totalHours.toLocaleString()}{" "}
                                    <span className="text-[14px] text-slate-400 font-medium">
                                        hrs
                                    </span>
                                </h2>
                                <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Across selected period</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center shrink-0">
                                <Briefcase className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-emerald-600" />
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5">Projects</p>
                                <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">
                                    {stats.totalProjects}
                                </h2>
                                <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">Active projects</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                            <div className="w-10 h-10 rounded-2xl bg-rose-50 flex items-center justify-center shrink-0">
                                <AlertCircle className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-rose-600" />
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <p className="text-[10px] sm:text-xs font-medium text-slate-500 truncate mb-0.5">Employees Pending</p>
                                <h2 className="text-xl lg:text-2xl font-bold text-slate-900 leading-none truncate">
                                    {stats.pendingEmployees}{" "}
                                    <span className="text-[14px] text-slate-400 font-medium">
                                        Employees
                                    </span>
                                </h2>
                                <p className="text-[10px] font-medium text-rose-500 mt-0.5 truncate">Needs Follow-up</p>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Charts Row */}
            {isApiLoading ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <Skeleton variant="rounded" width="100%" height={260} sx={{ borderRadius: 3 }} />
                    <Skeleton variant="rounded" width="100%" height={260} sx={{ borderRadius: 3 }} />
                </div>
            ) : rawTimesheetData.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {/* Project Distribution Pie Chart */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3 relative">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-[13px] font-bold text-slate-800">
                                Project Distribution
                            </h3>
                            {projectDistribution.length > 7 && (
                                <button
                                    onClick={() => setAllProjectsModalOpen(true)}
                                    className="text-[11px] font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-md transition-colors"
                                >
                                    View All Projects
                                </button>
                            )}
                        </div>
                        <div className="flex-1 flex flex-col h-[220px] mt-6">
                            <div className="flex flex-row items-center justify-between flex-1 min-h-0">
                                <section className="w-[45%] flex items-center justify-center min-h-[200px] h-full relative isolate">
                                    {pieData?.chartData && (
                                        <>
                                            <div className="relative z-10 w-full h-[190px] flex items-center justify-center">
                                                <Doughnut data={pieData.chartData} options={pieOptions} />
                                            </div>
                                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none -z-10 mt-1">
                                                <span className="text-2xl font-bold text-slate-800 leading-none">
                                                    {pieData.totalHours}h
                                                </span>
                                                <span className="text-[10px] text-slate-500 mt-1">
                                                    Total Hours
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </section>

                                {pieData?.legendData && pieData.legendData.length > 0 && (
                                    <div className="w-[55%] pl-2 flex flex-col gap-2.5">
                                        {pieData.legendData.map((item, idx) => (
                                            <div key={idx} className="flex items-center justify-between">
                                                <div className="flex items-center gap-3 min-w-0 pr-4">
                                                    <span className="w-2.5 h-2.5 rounded-full shadow-sm shrink-0" style={{ backgroundColor: item.color }}></span>
                                                    <span className="text-[12px] text-slate-600 font-medium truncate" title={item.name}>{item.name}</span>
                                                </div>
                                                <div className="flex items-center shrink-0">
                                                    <span className="text-[11px] font-bold text-slate-900">{item.value} hrs</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Top Task Types Bar Chart */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3">
                        <h3 className="text-[13px] font-bold text-slate-800 mb-2">Top Task Types</h3>
                        <div className="h-[260px] w-full">
                            {barChartData && <Bar data={barChartData} options={barOptions} />}
                        </div>
                    </div>
                </div>
            )}

            {/* Reusable Table */}
            <TimesheetSummaryTable 
                rawTimesheetData={rawTimesheetData} 
                projects={projects} 
                employees={employees}
                isApiLoading={isApiLoading}
                showFiltersToggle={false}
            />

            {/* All Projects Modal */}
            <AllProjectsModal
                isOpen={isAllProjectsModalOpen}
                onClose={() => setAllProjectsModalOpen(false)}
                projectsList={projectDistribution}
                totalHours={projectDistribution.reduce((acc, p) => acc + p.hours, 0)}
            />
        </div>
    );
}